import React, { useState } from "react";
import {
  UserCircle2, Activity, DollarSign, Copy, Check, Shield,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import AuthModal from "./AuthModal";
import WhistleblowerModal from "./WhistleblowerModal";

export const Footer = () => {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [whistleOpen, setWhistleOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Cash App cashtag — the `$` is literal (it's part of Cash App's username format)
  const CASHTAG = "$adminvault";
  const CASHAPP_URL = "https://cash.app/$adminvault";

  const copyCashtag = async () => {
    try {
      await navigator.clipboard.writeText(CASHTAG);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore — older browser */ }
  };

  return (
    <footer data-testid="footer" className="mt-32 border-t border-white/10 bg-[#050505] no-print">
      {/* Single row across desktop — TITLE · SIGN UP · CASHAPP. Stacks on mobile. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 lg:gap-10">

        {/* === 1. Brand title === */}
        <div className="shrink-0 max-w-xs">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 border border-[#ff3b30] flex items-center justify-center">
              <span className="font-mono text-[#ff3b30] text-xs font-bold">VJ</span>
            </div>
            <div>
              <div className="font-heading font-black uppercase tracking-tight text-white text-base">
                V. Jones Vault
              </div>
              <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 mt-0.5">
                DEEP / ARCHIVE / 2026
              </div>
            </div>
          </div>
          <p className="font-mono text-[11px] tracking-wider text-zinc-500 leading-relaxed">
            CURATED BY V. JONES — INVESTIGATIVE / NEUTRAL / SOURCED
          </p>
        </div>

        {/* === 2. Auth CTA === */}
        <div className="shrink-0 max-w-xs">
          {!user ? (
            <>
              <button
                type="button"
                onClick={() => setAuthOpen(true)}
                data-testid="footer-signup-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-purple-500 text-purple-300 hover:bg-purple-500 hover:text-[#0a0a0a] font-mono text-[11px] uppercase tracking-[0.22em] transition-colors"
              >
                <UserCircle2 size={13} /> Optional Sign Up
              </button>
              <p className="font-mono text-[9px] tracking-wider text-zinc-600 mt-2 leading-relaxed">
                No email or phone — just a username + recovery code. Anonymous browsing keeps working
                without an account.
              </p>
            </>
          ) : (
            <div data-testid="footer-signed-in" className="inline-flex items-center gap-2 px-4 py-2.5 border border-purple-500/50 text-purple-300 font-mono text-[11px] uppercase tracking-[0.22em]">
              <UserCircle2 size={13} /> Signed in as {user.username}
            </div>
          )}
        </div>

        {/* === 3. Cash App donate === */}
        <div data-testid="footer-donate-block" className="shrink-0 max-w-xs">
          <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2 flex items-center gap-2">
            <DollarSign size={11} className="text-[#00ff88]" />
            // Keep the lights on
          </div>
          <p className="font-mono text-[10px] tracking-wider text-zinc-500 leading-relaxed mb-3">
            No ads. No paywall. No tracking.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={CASHAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cashapp-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-[#00ff88] bg-[#00ff88]/[0.06] text-[#00ff88] hover:bg-[#00ff88] hover:text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold transition-colors"
            >
              {CASHTAG}
            </a>
            <button
              type="button"
              onClick={copyCashtag}
              data-testid="footer-cashapp-copy"
              aria-label="Copy Cash App tag"
              title="Copy cashtag"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 border-2 border-white/15 text-zinc-400 hover:text-white hover:border-white/40 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              {copied ? <><Check size={12} className="text-[#00ff88]" /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
        </div>

        {/* === 4. Tip & Issue Drop === */}
        <div data-testid="footer-whistleblower-block" className="shrink-0 max-w-xs">
          <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2 flex items-center gap-2">
            <Shield size={11} className="text-[#ff3b30]" />
            // Got a tip or an issue?
          </div>
          <p className="font-mono text-[10px] tracking-wider text-zinc-500 leading-relaxed mb-3">
            Anonymous. No IP, no account. Drop a lead, a bug report, or a feature idea.
          </p>
          <button
            type="button"
            onClick={() => setWhistleOpen(true)}
            data-testid="footer-whistleblower-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-[#ff3b30] text-[#ff3b30] hover:bg-[#ff3b30] hover:text-black font-mono text-[11px] uppercase tracking-[0.2em] font-bold transition-colors"
          >
            <Shield size={13} /> Tips &amp; Issues
          </button>
        </div>

      </div>
      <div className="border-t border-white/10 px-4 sm:px-8 py-4 max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
        <span className="font-mono text-[10px] text-zinc-500 tracking-wider flex items-center gap-3">
          © 2026 — V. JONES VAULT // ARCHIVE NODE
          {/* Hidden Tarot Deck Forge sigil — clickable hexagon, no label. */}
          <a
            href="/forge"
            title=""
            aria-label="forge"
            data-testid="footer-forge-sigil"
            className="inline-flex items-center text-zinc-700 hover:text-violet-300 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 100 100" aria-hidden="true">
              <polygon points="50,8 88,28 88,72 50,92 12,72 12,28"
                fill="none" stroke="currentColor" strokeWidth="6" />
              <circle cx="50" cy="50" r="4" fill="currentColor" />
            </svg>
          </a>
        </span>
        <span className="inline-flex items-center gap-2 font-mono text-[10px] text-zinc-500 tracking-wider">
          <Activity size={10} className="text-[#00ff88] animate-pulse" />
          STATUS: <span className="text-[#00ff88]">ONLINE</span>
        </span>
      </div>
      <AuthModal open={authOpen} initialMode="signup" onClose={() => setAuthOpen(false)} />
      <WhistleblowerModal open={whistleOpen} onClose={() => setWhistleOpen(false)} />
    </footer>
  );
};

export default Footer;
